/**
 * "Metrics" section for the product item drawer.
 *
 * Lists the metrics associated with this product card as paper cards on the
 * drawer's schematic grid. A/B experiment metrics (`<root>.treatment` /
 * `<root>.control`) fold into a single paired card with a highlighted delta;
 * everything else renders standalone. Each card shows an authored title,
 * machine name, latest value, and a sparkline, and expands to a full
 * time-series chart. Supports collecting a metric on demand and linking /
 * unlinking metrics. Superadmin only — metrics are a superadmin domain and the
 * lookups hit superadmin endpoints.
 */
import React from 'react';
import {
  Button,
  Dialog,
  DialogTrigger,
  Flex,
  Text,
  TextField,
  useToast,
} from '@chatsdet/ui';
import { ChartLine, Plus } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import { SectionLabel } from '../../cards/components/SectionLabel';
import {
  useAllMetrics,
  useCardSparklines,
  useLinkMetricToCard,
  useProductCardMetrics,
} from '../hooks/useProductCardMetrics';
import { groupMetrics } from './metricPairing';
import { MetricGroupCard } from './MetricGroupCard';

interface Props {
  productItemId: string;
}

export function ProductItemMetricsSection({ productItemId }: Props) {
  const { user } = useAuth();
  const isSuperadmin = user?.isSuperadmin === true;
  const { toast } = useToast();

  const metricsQuery = useProductCardMetrics(isSuperadmin ? productItemId : null);
  const sparklinesQuery = useCardSparklines(isSuperadmin);
  const linkMetric = useLinkMetricToCard(productItemId);
  const [pickerOpen, setPickerOpen] = React.useState(false);

  // Map this card's batched sparkline series by metric name — always available,
  // independent of which rows are expanded.
  const sparkByName = React.useMemo(() => {
    const map = new Map<string, number[]>();
    for (const s of sparklinesQuery.data ?? []) {
      if (s.cardId === productItemId) map.set(s.metricName, s.values);
    }
    return map;
  }, [sparklinesQuery.data, productItemId]);

  if (!isSuperadmin) return null;

  const metrics = metricsQuery.data ?? [];
  const groups = groupMetrics(metrics);

  const unlink = (name: string) => {
    linkMetric.mutate(
      { name, action: 'unlink' },
      {
        onSuccess: () => toast.success(`Unlinked ${name}`),
        onError: (err) => toast.error(err.message),
      }
    );
  };

  return (
    <section className="space-y-3 border-t border-border pt-6">
      <Flex align="center" justify="between">
        <SectionLabel>Metrics</SectionLabel>
        <DialogTrigger isOpen={pickerOpen} onOpenChange={setPickerOpen}>
          <Button variant="ghost" size="sm" iconLeft={<Plus size={13} weight="bold" />}>
            Link metric
          </Button>
          <Dialog
            title="Link a metric"
            description="Associate a metric with this product card. Its sparkline and latest value will show here and on the card."
          >
            <MetricPicker
              linkedNames={metrics.map((m) => m.name)}
              onPick={(name) =>
                linkMetric.mutate(
                  { name, action: 'link' },
                  {
                    onSuccess: () => {
                      toast.success(`Linked ${name}`);
                      setPickerOpen(false);
                    },
                    onError: (err) => toast.error(err.message),
                  }
                )
              }
            />
          </Dialog>
        </DialogTrigger>
      </Flex>

      {groups.length === 0 ? (
        <Text size="xs" className="text-muted-foreground">
          No metrics linked. Define metrics with <span className="font-mono">cofactor metrics</span>.
        </Text>
      ) : (
        <div className="space-y-2.5">
          {groups.map((group) => (
            <MetricGroupCard
              key={group.key}
              group={group}
              productItemId={productItemId}
              sparkByName={sparkByName}
              onUnlink={unlink}
              unlinkPending={linkMetric.isPending}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/** Searchable picker over the org's metrics not already linked to this card. */
function MetricPicker({
  linkedNames,
  onPick,
}: {
  linkedNames: string[];
  onPick: (name: string) => void;
}) {
  const allMetricsQuery = useAllMetrics(true);
  const [search, setSearch] = React.useState('');

  const candidates = React.useMemo(() => {
    const all = allMetricsQuery.data ?? [];
    const term = search.trim().toLowerCase();
    return all
      .filter((m) => !linkedNames.includes(m.name))
      .filter((m) => (term ? m.name.toLowerCase().includes(term) : true))
      .slice(0, 25);
  }, [allMetricsQuery.data, linkedNames, search]);

  return (
    <div className="space-y-3">
      <TextField
        aria-label="Search metrics"
        placeholder="Search metrics…"
        value={search}
        onChange={setSearch}
        autoFocus
      />
      <div className="max-h-72 space-y-0.5 overflow-y-auto">
        {allMetricsQuery.isLoading ? (
          <Text size="xs" className="text-muted-foreground">
            Loading metrics…
          </Text>
        ) : candidates.length === 0 ? (
          <Text size="xs" className="text-muted-foreground">
            No matching metrics.
          </Text>
        ) : (
          candidates.map((metric) => (
            <button
              key={metric.id}
              type="button"
              onClick={() => onPick(metric.name)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted"
            >
              <ChartLine size={12} className="shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate font-mono text-xs">{metric.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{metric.source}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
